import { useEffect, useMemo, useState } from "react";
import { Pressable, StyleSheet, View, LayoutAnimation } from "react-native";
import { useTranslation } from "react-i18next";
import { Link2, ChevronDown, TriangleAlert, ExternalLink, Info as LucideInfo } from "lucide-react";
import { CopySimpleIcon, CheckIcon, InfoIcon } from "@phosphor-icons/react";
import { useGetLtiConfigForSchoolAdminQuery } from "@axio/graphql/generated/graphql";

import CustomText from "../../components/common/general/CustomText/CustomText";
import CustomTextInput from "../../components/common/general/CustomTextInput/CustomTextInput";
import CustomSwitch from "../../components/common/general/CustomSwitch/CustomSwitch";
import CustomButton from "../../components/common/general/CustomButton/CustomButton";
import CustomSpacing from "../../components/common/layout/CustomSpacing/CustomSpacing";

type AdminLtiSettingsContainerProps = {
  ltiActivitiesEnabled?: boolean | null;
  tenantDomain?: string | null;
  onUpdateSettings: (options: { variables: { settings: { ltiActivitiesEnabled?: boolean } } }) => Promise<unknown>;
};

type ToolConfigKey = "toolUrl" | "oidcUrl" | "jwksUrl" | "redirectUri";

function buildToolConfig(tenantDomain?: string | null) {
  const base = tenantDomain ? `https://${tenantDomain}` : "https://app.axio.ai";
  return {
    toolUrl: `${base}/api/auth/lti/login`,
    oidcUrl: `${base}/api/auth/lti/login`,
    jwksUrl: `${base}/api/auth/lti/jwk`,
    redirectUri: `${base}/api/auth/lti/oauth`,
  };
}

function AdminLtiSettingsContainer({
  ltiActivitiesEnabled,
  tenantDomain,
  onUpdateSettings,
}: AdminLtiSettingsContainerProps) {
  const { t } = useTranslation();
  const [copiedKey, setCopiedKey] = useState<ToolConfigKey | null>(null);
  const [ileEnabled, setIleEnabled] = useState(Boolean(ltiActivitiesEnabled));
  const [setupGuideOpen, setSetupGuideOpen] = useState(false);

  const { data: ltiConfigData } = useGetLtiConfigForSchoolAdminQuery();
  const ltiConfig = ltiConfigData?.getLtiConfigForSchoolAdmin?.data;

  const toolConfig = useMemo(() => buildToolConfig(tenantDomain), [tenantDomain]);

  const toggleSetupGuide = () => {
    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    setSetupGuideOpen((prev) => !prev);
  };

  useEffect(() => {
    setIleEnabled(Boolean(ltiActivitiesEnabled));
  }, [ltiActivitiesEnabled]);

  const isConfigured = Boolean(ltiActivitiesEnabled);
  const statusLabel = isConfigured ? t("buttons.lti_status_configured") : t("buttons.lti_status_not_configured");
  const statusDescription = isConfigured
    ? t("buttons.lti_status_enabled_description")
    : t("buttons.lti_status_not_configured_description");

  const toolConfigRows = useMemo(
    () => [
      { key: "toolUrl" as const, label: t("buttons.lti_tool_url"), value: toolConfig.toolUrl },
      { key: "oidcUrl" as const, label: t("buttons.lti_openid_connect_login_url"), value: toolConfig.oidcUrl },
      { key: "jwksUrl" as const, label: t("buttons.lti_public_keyset_jwks_url"), value: toolConfig.jwksUrl },
      { key: "redirectUri" as const, label: t("buttons.lti_redirect_uri"), value: toolConfig.redirectUri },
    ],
    [t, toolConfig]
  );

  const handleCopy = async (key: ToolConfigKey, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1500);
    } catch {
      setCopiedKey(null);
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.card}>
        <View style={styles.headerRow}>
          <View style={[styles.headerIcon, { backgroundColor: "#E0F2FE" }]}>
            <Link2 size={20} color="#0369A1" />
          </View>
          <View style={styles.headerContent}>
            <View style={styles.headerTitleRow}>
              <CustomText
                text={t("buttons.lti_tool_registration")}
                size="xxl"
                weight="bold"
                useTranslationText={false}
                style={styles.headerTitle}
              />
              <View
                style={[styles.statusBadge, isConfigured ? styles.statusBadgeConfigured : styles.statusBadgeWarning]}>
                <CustomText
                  text={statusLabel}
                  size="xs"
                  weight="bold"
                  useTranslationText={false}
                  style={isConfigured ? styles.statusBadgeConfiguredText : styles.statusBadgeWarningText}
                />
              </View>
            </View>
            <CustomSpacing type="vertical" size="xs" />
            <CustomText
              text={t("buttons.lti_tool_registration_description")}
              size="s"
              useTranslationText={false}
              style={styles.headerDescription}
            />
          </View>
        </View>

        <CustomSpacing type="vertical" size="l" />

        <View style={[styles.callout, isConfigured ? styles.calloutConfigured : styles.calloutWarning]}>
          {isConfigured ? (
            <InfoIcon size={20} color="#0369A1" weight="fill" style={styles.calloutIcon} />
          ) : (
            <TriangleAlert size={20} color="#B54708" style={styles.calloutIcon} />
          )}
          <View style={styles.calloutContent}>
            <CustomText
              text={statusLabel}
              size="s"
              weight="bold"
              useTranslationText={false}
              style={isConfigured ? styles.calloutConfiguredTitle : styles.calloutWarningTitle}
            />
            <CustomSpacing type="vertical" size="xs" />
            <CustomText
              text={statusDescription}
              size="s"
              useTranslationText={false}
              style={isConfigured ? styles.calloutConfiguredText : styles.calloutWarningTextBody}
            />
          </View>
        </View>

        <CustomSpacing type="vertical" size="l" />

        <View style={styles.setupGuideWrapper}>
          <Pressable
            onPress={toggleSetupGuide}
            accessibilityRole="button"
            accessibilityState={{ expanded: setupGuideOpen }}
            style={({ pressed }) => [styles.setupGuideHeader, pressed && styles.setupGuideHeaderPressed]}>
            <View style={styles.setupGuideHeaderLeft}>
              <InfoIcon size={16} color="#667085" weight="regular" />
              <CustomText
                text={t("buttons.lti_setup_guide_title")}
                size="s"
                weight="medium"
                useTranslationText={false}
                style={styles.setupGuideTitle}
              />
            </View>
            <View style={{ transform: [{ rotate: setupGuideOpen ? "180deg" : "0deg" }] }}>
              <ChevronDown size={16} color="#667085" aria-hidden />
            </View>
          </Pressable>
          {setupGuideOpen && (
            <View style={styles.setupGuideBody}>
              {[
                t("buttons.lti_setup_step_1"),
                t("buttons.lti_setup_step_2"),
                t("buttons.lti_setup_step_3"),
                t("buttons.lti_setup_step_4"),
              ].map((step, i) => (
                <View key={i} style={styles.setupGuideStep}>
                  <CustomText text={step} size="xs" useTranslationText={false} style={styles.setupGuideStepText} />
                </View>
              ))}
            </View>
          )}
        </View>

        <CustomSpacing type="vertical" size="l" />

        <View style={styles.sectionCard}>
          <CustomText
            text={t("buttons.lti_axio_tool_configuration")}
            size="m"
            weight="bold"
            useTranslationText={false}
            style={styles.sectionTitle}
          />
          <CustomSpacing type="vertical" size="xs" />
          <CustomText
            text={t("buttons.lti_copy_values_description")}
            size="xs"
            useTranslationText={false}
            style={styles.sectionDescription}
          />

          <CustomSpacing type="vertical" size="sm" />

          <View style={styles.configList}>
            {toolConfigRows.map((row) => (
              <View key={row.key} style={styles.configRow}>
                <View style={styles.configTextBlock}>
                  <CustomText text={row.label} size="xs" useTranslationText={false} style={styles.configLabel} />
                  <CustomText text={row.value} size="s" useTranslationText={false} style={styles.configValue} />
                </View>
                <Pressable
                  onPress={() => handleCopy(row.key, row.value)}
                  accessibilityRole="button"
                  accessibilityLabel={
                    copiedKey === row.key
                      ? t("buttons.lti_copied_label", { label: row.label })
                      : t("buttons.lti_copy_label", { label: row.label })
                  }
                  style={({ pressed }) => [styles.copyButton, pressed && styles.copyButtonPressed]}>
                  {copiedKey === row.key ? (
                    <CheckIcon size={18} color="#059669" weight="bold" />
                  ) : (
                    <CopySimpleIcon size={18} color="#667085" weight="regular" />
                  )}
                </Pressable>
              </View>
            ))}
          </View>
        </View>

        <CustomSpacing type="vertical" size="l" />

        <View style={styles.sectionCard}>
          <CustomText
            text={t("buttons.lti_lms_platform_credentials")}
            size="m"
            weight="bold"
            useTranslationText={false}
            style={styles.sectionTitle}
          />
          <CustomSpacing type="vertical" size="xs" />
          <CustomText
            text={t("buttons.lti_enter_values_description")}
            size="xs"
            useTranslationText={false}
            style={styles.sectionDescription}
          />

          <CustomSpacing type="vertical" size="sm" />

          <View style={styles.readOnlyNotice}>
            <CustomText
              text={t("buttons.lti_credentials_configured_by_admin")}
              size="xs"
              useTranslationText={false}
              style={styles.readOnlyNoticeText}
            />
          </View>

          <CustomSpacing type="vertical" size="sm" />

          <View style={styles.inputStack}>
            <View>
              <CustomText
                text={t("buttons.lti_platform_url")}
                size="s"
                weight="medium"
                useTranslationText={false}
                style={styles.inputLabel}
              />
              <CustomSpacing type="vertical" size="xs" />
              <CustomTextInput
                value={ltiConfig?.platformUrl || ""}
                onChangeText={() => {}}
                placeholder={"https://yourschool.instructure.com"}
                editable={false}
                helperText={t("buttons.lti_platform_url_helper")}
                useTranslationText={false}
                style={styles.readOnlyInput}
              />
            </View>

            <View>
              <CustomText
                text={t("buttons.lti_client_id")}
                size="s"
                weight="medium"
                useTranslationText={false}
                style={styles.inputLabel}
              />
              <CustomSpacing type="vertical" size="xs" />
              <CustomTextInput
                value={ltiConfig?.clientId || ""}
                onChangeText={() => {}}
                placeholder={"10000000000001"}
                editable={false}
                helperText={t("buttons.lti_client_id_helper")}
                useTranslationText={false}
                style={styles.readOnlyInput}
              />
            </View>

            <View>
              <CustomText
                text={t("buttons.lti_deployment_id")}
                size="s"
                weight="medium"
                useTranslationText={false}
                style={styles.inputLabel}
              />
              <CustomSpacing type="vertical" size="xs" />
              <CustomTextInput
                value={ltiConfig?.deploymentId || ""}
                onChangeText={() => {}}
                placeholder={"1:abc123def456"}
                editable={false}
                helperText={t("buttons.lti_deployment_id_helper")}
                useTranslationText={false}
                style={styles.readOnlyInput}
              />
            </View>
          </View>

          <View style={styles.registerButtonRow}>
            <View
              accessibilityRole="button"
              accessibilityLabel={t("buttons.lti_register_tool_tooltip")}
              // @ts-expect-error -- web-only title attribute for hover tooltip
              title={t("buttons.lti_register_tool_tooltip")}>
              <CustomButton
                text={t("buttons.lti_register_tool")}
                styleType="primary"
                size="m"
                bold={true}
                onPress={() => {}}
                disabled={true}
                useTranslationText={false}
                leftIcon={<Link2 size={18} color="#FFFFFF" />}
              />
            </View>
          </View>
        </View>

        <CustomSpacing type="vertical" size="l" />

        <View style={styles.sectionCard}>
          <View style={styles.activityHeaderRow}>
            <View style={[styles.headerIcon, { backgroundColor: "#F3E8FF" }]}>
              <ExternalLink size={20} color="#7C3AED" />
            </View>
            <View style={styles.headerContent}>
              <CustomText
                text={t("buttons.lti_available_activity_types")}
                size="m"
                weight="bold"
                useTranslationText={false}
                style={styles.sectionTitle}
              />
              <CustomSpacing type="vertical" size="xs" />
              <CustomText
                text={t("buttons.lti_available_activity_types_description")}
                size="xs"
                useTranslationText={false}
                style={styles.sectionDescription}
              />
            </View>
          </View>

          <CustomSpacing type="vertical" size="sm" />

          <View style={styles.toggleList}>
            <View style={styles.toggleRow}>
              <View style={styles.toggleText}>
                <CustomText
                  text={t("buttons.lti_interactive_learning_experiences")}
                  size="s"
                  weight="medium"
                  useTranslationText={false}
                  style={styles.toggleTitle}
                />
                <CustomSpacing type="vertical" size="xs" />
                <CustomText
                  text={t("buttons.lti_interactive_learning_description")}
                  size="xs"
                  useTranslationText={false}
                  style={styles.toggleDescription}
                />
              </View>
              <CustomSwitch
                value={ileEnabled}
                onValueChange={(value) => {
                  setIleEnabled(value);
                  onUpdateSettings({
                    variables: { settings: { ltiActivitiesEnabled: value } },
                  });
                }}
                accessibilityLabel={t("buttons.lti_enable_interactive_learning")}
              />
            </View>
          </View>
        </View>

        <CustomSpacing type="vertical" size="l" />

        <View style={styles.infoCard}>
          <LucideInfo size={18} color="#075985" style={styles.calloutIcon} />
          <View style={styles.calloutContent}>
            <CustomText
              text={t("buttons.lti_automatic_user_provisioning")}
              size="m"
              weight="bold"
              useTranslationText={false}
              style={styles.sectionTitle}
            />
            <CustomSpacing type="vertical" size="xs" />
            <CustomText
              text={t("buttons.lti_automatic_user_provisioning_description")}
              size="s"
              useTranslationText={false}
              style={styles.sectionDescription}
            />
            <CustomSpacing type="vertical" size="sm" />
            <CustomText
              text={t("buttons.lti_provisioning_bullet_1")}
              size="s"
              useTranslationText={false}
              style={styles.infoListItem}
            />
            <CustomText
              text={t("buttons.lti_provisioning_bullet_2")}
              size="s"
              useTranslationText={false}
              style={styles.infoListItem}
            />
            <CustomText
              text={t("buttons.lti_provisioning_bullet_3")}
              size="s"
              useTranslationText={false}
              style={styles.infoListItem}
            />
            <CustomText
              text={t("buttons.lti_provisioning_bullet_4")}
              size="s"
              useTranslationText={false}
              style={styles.infoListItem}
            />
          </View>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  card: {
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
    borderWidth: 1,
    borderColor: "#E4E7EC",
    padding: 24,
  },
  headerRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 16,
  },
  activityHeaderRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 16,
  },
  headerIcon: {
    width: 40,
    height: 40,
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
  },
  headerContent: {
    flex: 1,
  },
  headerTitleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },
  headerTitle: {
    color: "#101828",
  },
  headerDescription: {
    color: "#475467",
    lineHeight: 20,
  },
  statusBadge: {
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 4,
  },
  statusBadgeConfigured: {
    backgroundColor: "#ECFDF3",
  },
  statusBadgeWarning: {
    backgroundColor: "#FFFAEB",
  },
  statusBadgeConfiguredText: {
    color: "#027A48",
  },
  statusBadgeWarningText: {
    color: "#B54708",
  },
  callout: {
    borderRadius: 12,
    borderWidth: 1,
    padding: 16,
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 12,
  },
  calloutConfigured: {
    backgroundColor: "#F0F9FF",
    borderColor: "#7DD3FC",
  },
  calloutWarning: {
    backgroundColor: "#FFFAEB",
    borderColor: "#FEC84B",
  },
  calloutIcon: {
    marginTop: 2,
  },
  calloutContent: {
    flex: 1,
  },
  calloutConfiguredTitle: {
    color: "#075985",
  },
  calloutConfiguredText: {
    color: "#0C4A6E",
  },
  calloutWarningTitle: {
    color: "#B54708",
  },
  calloutWarningTextBody: {
    color: "#7A2E0E",
  },
  setupGuideWrapper: {
    borderWidth: 1,
    borderColor: "#E4E7EC",
    borderRadius: 12,
    overflow: "hidden",
    backgroundColor: "#FFFFFF",
  },
  setupGuideHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    padding: 16,
  },
  setupGuideHeaderPressed: {
    backgroundColor: "#F9FAFB",
  },
  setupGuideHeaderLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    flex: 1,
  },
  setupGuideTitle: {
    color: "#667085",
  },
  setupGuideBody: {
    borderTopWidth: 1,
    borderTopColor: "#E4E7EC",
    padding: 16,
    backgroundColor: "#F9FAFB",
    gap: 12,
  },
  setupGuideStep: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
  },
  setupGuideStepText: {
    flex: 1,
    color: "#344054",
    lineHeight: 18,
  },
  sectionCard: {
    borderWidth: 1,
    borderColor: "#E4E7EC",
    borderRadius: 12,
    padding: 16,
    backgroundColor: "#FFFFFF",
  },
  sectionTitle: {
    color: "#101828",
  },
  sectionDescription: {
    color: "#667085",
    lineHeight: 18,
  },
  configList: {
    gap: 8,
  },
  configRow: {
    borderWidth: 1,
    borderColor: "#E4E7EC",
    borderRadius: 10,
    backgroundColor: "#F9FAFB",
    padding: 12,
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  configTextBlock: {
    flex: 1,
    minWidth: 0,
  },
  configLabel: {
    color: "#667085",
    marginBottom: 4,
  },
  configValue: {
    color: "#101828",
  },
  copyButton: {
    width: 36,
    height: 36,
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
  },
  copyButtonPressed: {
    backgroundColor: "#EFF8FF",
  },
  inputStack: {
    gap: 16,
  },
  inputLabel: {
    color: "#344054",
  },
  readOnlyInput: {
    backgroundColor: "#F9FAFB",
    color: "#667085",
  },
  readOnlyNotice: {
    backgroundColor: "#F9FAFB",
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  readOnlyNoticeText: {
    color: "#667085",
    fontStyle: "italic",
  },
  registerButtonRow: {
    marginTop: 16,
    alignItems: "flex-end",
  },
  toggleList: {
    gap: 12,
  },
  toggleRow: {
    borderWidth: 1,
    borderColor: "#E4E7EC",
    borderRadius: 12,
    backgroundColor: "#F9FAFB",
    padding: 16,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  toggleText: {
    flex: 1,
    marginRight: 12,
  },
  toggleTitle: {
    color: "#101828",
  },
  toggleDescription: {
    color: "#667085",
    lineHeight: 18,
  },
  infoCard: {
    backgroundColor: "#F0F9FF",
    borderWidth: 1,
    borderColor: "#7DD3FC",
    borderRadius: 12,
    padding: 16,
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 12,
  },
  infoListItem: {
    color: "#475467",
    lineHeight: 18,
  },
});

export default AdminLtiSettingsContainer;
